import * as React from "react"
import { X, Plus } from "lucide-react"

import { cn } from "../../../lib/utils"
import { Checkbox } from "../checkbox/checkbox"
import { Skeleton } from "../skeleton/skeleton"
import { Popover, PopoverAnchor, PopoverContent } from "../popover/popover"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../tooltip/tooltip"

// ─── Public types ───────────────────────────────────────────────────

export interface MultiTagInputOption {
  /** Stable unique key — never shown in the UI */
  id: string
  /** Display label shown in the dropdown and as the tag chip */
  label: string
  /** Stored value written into the value[] array. Defaults to label when omitted. */
  value?: string
  /** Optional image URL rendered to the left of the label in the dropdown */
  imageUrl?: string
  /** Initials (or short text) shown when `imageUrl` is missing. */
  imageFallback?: string
  /** Secondary line under the label (truncated in the list; full text in tooltip). */
  description?: string
  /** Status marker rendered beside the label. Mark the exception, not every row. */
  badge?: React.ReactNode
  /**
   * Longer context shown only in the hover tooltip, under `description`. Lets the row
   * keep one terse scannable line without losing the full text.
   */
  detail?: string
  /**
   * Optional section label. Consecutive options sharing the same `group` render
   * under a non-selectable header. Caller owns order (e.g. core rows, then industry).
   */
  group?: string
}

export interface MultiTagInputProps {
  /** Pre-defined options shown in the dropdown. */
  options?: MultiTagInputOption[]
  /** Controlled array of selected values (option labels and/or free-text). */
  value?: string[]
  onChange?: (values: string[]) => void
  /** Shown when the field is empty. */
  placeholder?: string
  /** Dropdown message when there are no matching options and no pending text. */
  noOptionsText?: string
  /** Label for the "add free text" row. Receives the trimmed input. */
  addFreeTextLabel?: (text: string) => string
  loading?: boolean
  disabled?: boolean
  /** Suppress the dropdown entirely — useful for free-text-only tag inputs with no predefined options. */
  disableDropdown?: boolean
  /** Only allow selecting from predefined options — prevent adding arbitrary free-text tags. */
  disableFreeText?: boolean
  /** Optional formatter applied to stored values when rendering tag chips. */
  renderTag?: (value: string) => string
  /** Optional bulk-action bar rendered at the top of the dropdown. */
  bulkActions?: {
    selectedOfTotalLabel: string
    selectAllLabel: string
    clearLabel: string
    onSelectAll: () => void
    onClear: () => void
    showSelectAll?: boolean
  }
  /** Shown when the active filter yields zero options. Receives the trimmed query. */
  noMatchText?: (query: string) => string
  /** Forwarded to the hidden `<input>` element for `<label htmlFor>` wiring. */
  id?: string
  className?: string
  "data-testid"?: string
}

// ─── Main component ─────────────────────────────────────────────────

export function MultiTagInput({
  options = [],
  value = [],
  onChange,
  placeholder = "Type and press Enter…",
  noOptionsText = "No options",
  addFreeTextLabel = (text) => `Add "${text}"`,
  loading = false,
  disabled = false,
  disableDropdown = false,
  disableFreeText = false,
  renderTag,
  bulkActions,
  noMatchText,
  id,
  className,
  "data-testid": testId,
}: MultiTagInputProps) {
  const [inputValue, setInputValue] = React.useState("")
  const [open, setOpen] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const inputRef = React.useRef<HTMLInputElement>(null)

  function isInsideAnchor(target: EventTarget | null): boolean {
    return target instanceof Node && !!containerRef.current?.contains(target)
  }

  const trimmedInput = inputValue.trim()

  const filteredOptions = React.useMemo(
    () =>
      trimmedInput
        ? options.filter((opt) =>
            opt.label.toLowerCase().includes(trimmedInput.toLowerCase()),
          )
        : options,
    [options, trimmedInput],
  )

  const dropdownItems = React.useMemo(
    () => buildGroupedDropdownItems(filteredOptions),
    [filteredOptions],
  )

  const optionByStoredValue = React.useMemo(() => {
    const map = new Map<string, MultiTagInputOption>()
    for (const opt of options) {
      map.set(opt.value ?? opt.label, opt)
    }
    return map
  }, [options])

  const isExactOptionMatch = options.some(
    (opt) => opt.label.toLowerCase() === trimmedInput.toLowerCase(),
  )
  const showAddRow = !!trimmedInput && !value.includes(trimmedInput) && !isExactOptionMatch && !disableFreeText
  const showNoMatch = !!trimmedInput && filteredOptions.length === 0 && !showAddRow && !!noMatchText
  const showDropdown = open && !disabled && !disableDropdown && (
    loading || filteredOptions.length > 0 || showAddRow || showNoMatch || options.length === 0
  )

  // ── Mutations ──────────────────────────────────────────────────────

  function addTag(tag: string) {
    const t = tag.trim()
    if (t && !value.includes(t)) {
      onChange?.([...value, t])
    }
    setInputValue("")
  }

  function removeTag(tag: string) {
    onChange?.(value.filter((v) => v !== tag))
  }

  function toggleOption(option: MultiTagInputOption) {
    const stored = option.value ?? option.label
    if (value.includes(stored)) {
      removeTag(stored)
    } else {
      addTag(stored)
    }
  }

  function commitInput() {
    if (trimmedInput && !disableFreeText) addTag(trimmedInput)
  }

  // ── Event handlers ─────────────────────────────────────────────────

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (!next) commitInput()
  }

  function handleContainerClick() {
    if (!disabled) {
      inputRef.current?.focus({ preventScroll: true })
      setOpen(true)
    }
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    setInputValue(e.target.value)
    if (!open) setOpen(true)
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      if (!trimmedInput) return
      const match = options.find(
        (opt) => opt.label.toLowerCase() === trimmedInput.toLowerCase(),
      )
      if (match) {
        toggleOption(match)
        setInputValue("")
      } else if (!disableFreeText) {
        addTag(trimmedInput)
      }
    } else if (e.key === "Backspace" && !inputValue && value.length > 0) {
      removeTag(value[value.length - 1])
    } else if (e.key === "Escape") {
      setOpen(false)
    } else if (!open) {
      setOpen(true)
    }
  }

  // ── Render ─────────────────────────────────────────────────────────

  return (
    <Popover open={showDropdown} onOpenChange={handleOpenChange}>
      <PopoverAnchor asChild>
        <div
          ref={containerRef}
          data-testid={testId}
          className={cn(
            "flex min-h-8 flex-wrap items-center gap-1 rounded-sm border border-input-border bg-input-background px-3 py-1 transition-colors cursor-text",
            "focus-within:border-input-focus-border",
            disabled && "cursor-not-allowed opacity-50",
            className,
          )}
          onClick={handleContainerClick}
        >
          {value.map((tag) => {
            const matchedOption = optionByStoredValue.get(tag)
            return (
              <TagChip
                key={tag}
                label={
                  renderTag
                    ? renderTag(tag)
                    : (matchedOption?.label ?? tag)
                }
                imageUrl={matchedOption?.imageUrl}
                imageFallback={matchedOption?.imageFallback}
                onRemove={() => removeTag(tag)}
                disabled={disabled}
              />
            )
          })}
          <input
            ref={inputRef}
            id={id}
            type="text"
            role="combobox"
            aria-expanded={showDropdown}
            aria-haspopup="listbox"
            aria-autocomplete="list"
            autoComplete="off"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setOpen(true)}
            onBlur={commitInput}
            placeholder={value.length === 0 ? placeholder : ""}
            disabled={disabled}
            className="flex-1 min-w-20 bg-transparent py-0.5 text-xs outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
          />
        </div>
      </PopoverAnchor>

      <PopoverContent
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (isInsideAnchor(e.target)) e.preventDefault()
        }}
        onPointerDownOutside={(e) => {
          if (isInsideAnchor(e.target)) e.preventDefault()
        }}
        className="w-(--radix-popover-trigger-width) p-0"
      >
        {loading ? (
          <DropdownLoading />
        ) : (
          <>
            {bulkActions ? (
              <DropdownBulkHeader
                selectedOfTotalLabel={bulkActions.selectedOfTotalLabel}
                selectAllLabel={bulkActions.selectAllLabel}
                clearLabel={bulkActions.clearLabel}
                onSelectAll={bulkActions.onSelectAll}
                onClear={bulkActions.onClear}
                showSelectAll={bulkActions.showSelectAll ?? true}
              />
            ) : null}
            <TooltipProvider delayDuration={250} skipDelayDuration={100}>
              <div
                role="listbox"
                aria-multiselectable="true"
                className="max-h-96 overflow-y-auto p-1"
                // A Dialog/Sheet scroll lock (react-remove-scroll) preventDefaults wheel
                // events on `document`. React portals listen on `body`, so stopping here
                // runs first and keeps this portalled list scrollable inside an open
                // dialog — same fix as `CommandList`. Scrolling by `deltaY` by hand
                // instead would land on top of the browser's own scroll everywhere the
                // lock is absent, moving the list twice per notch.
                onWheel={(e) => e.stopPropagation()}
              >
                {dropdownItems.map((item) => {
                  if (item.kind === "group") {
                    return <OptionGroupHeader key={`group:${item.label}`} label={item.label} />
                  }
                  const { option } = item
                  return (
                    <OptionRow
                      key={option.id}
                      label={option.label}
                      description={option.description}
                      badge={option.badge}
                      detail={option.detail}
                      imageUrl={option.imageUrl}
                      imageFallback={option.imageFallback}
                      selected={value.includes(option.value ?? option.label)}
                      onMouseDown={(e) => {
                        e.preventDefault() // keep input focused
                        toggleOption(option)
                      }}
                    />
                  )
                })}
                {showAddRow && (
                  <AddRow
                    label={addFreeTextLabel(trimmedInput)}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      addTag(trimmedInput)
                    }}
                  />
                )}
                {showNoMatch && noMatchText ? (
                  <p className="px-2 py-4 text-center text-sm text-muted-foreground">
                    {noMatchText(trimmedInput)}
                  </p>
                ) : null}
                {!loading && filteredOptions.length === 0 && !showAddRow && !showNoMatch ? (
                  <p className="px-2 py-4 text-center text-sm text-muted-foreground">
                    {noOptionsText}
                  </p>
                ) : null}
              </div>
            </TooltipProvider>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}

// ─── Sub-components ─────────────────────────────────────────────────

type DropdownItem =
  | { kind: "group"; label: string }
  | { kind: "option"; option: MultiTagInputOption }

/** Preserves caller order; inserts a header when `group` changes to a non-empty value. */
function buildGroupedDropdownItems(options: MultiTagInputOption[]): DropdownItem[] {
  const items: DropdownItem[] = []
  let lastGroup: string | undefined

  for (const option of options) {
    const group = option.group?.trim()
    if (group && group !== lastGroup) {
      items.push({ kind: "group", label: group })
      lastGroup = group
    } else if (!group) {
      lastGroup = undefined
    }
    items.push({ kind: "option", option })
  }

  return items
}

function OptionGroupHeader({ label }: { label: string }) {
  return (
    // `-top-1` cancels the listbox's `p-1`: sticky offsets resolve against the scrollport
    // inset by the scroll container's padding, so `top-0` would park the header 4px down
    // and leave a strip of scrolling rows visible above it.
    <div
      role="presentation"
      className="sticky -top-1 z-10 bg-popover px-2 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
    >
      {label}
    </div>
  )
}

function DropdownBulkHeader({
  selectedOfTotalLabel,
  selectAllLabel,
  clearLabel,
  onSelectAll,
  onClear,
  showSelectAll,
}: {
  selectedOfTotalLabel: string
  selectAllLabel: string
  clearLabel: string
  onSelectAll: () => void
  onClear: () => void
  showSelectAll: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-border px-2.5 py-2">
      <span className="text-xs text-muted-foreground">{selectedOfTotalLabel}</span>
      <div className="flex shrink-0 items-center gap-2.5">
        {showSelectAll ? (
          <button
            type="button"
            onMouseDown={(e) => {
              e.preventDefault()
              onSelectAll()
            }}
            className="text-xs font-medium text-primary transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:underline"
          >
            {selectAllLabel}
          </button>
        ) : null}
        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault()
            onClear()
          }}
          className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:underline"
        >
          {clearLabel}
        </button>
      </div>
    </div>
  )
}

function OptionAvatar({
  imageUrl,
  imageFallback,
  size,
}: {
  imageUrl?: string
  imageFallback?: string
  size: "sm" | "md"
}) {
  const sizeClass = size === "sm" ? "h-4 w-4 text-[8px]" : "h-5 w-5 text-[10px]"

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        className={cn(sizeClass, "shrink-0 rounded-full object-cover")}
      />
    )
  }

  if (!imageFallback) {
    return null
  }

  return (
    <span
      aria-hidden
      className={cn(
        sizeClass,
        "inline-flex shrink-0 items-center justify-center rounded-full bg-muted font-medium text-muted-foreground",
      )}
    >
      {imageFallback}
    </span>
  )
}

function TagChip({
  label,
  imageUrl,
  imageFallback,
  onRemove,
  disabled,
}: {
  label: string
  imageUrl?: string
  imageFallback?: string
  onRemove: () => void
  disabled?: boolean
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/50 px-2 py-0.5 text-xs font-medium text-foreground">
      <OptionAvatar imageUrl={imageUrl} imageFallback={imageFallback} size="sm" />
      <span className="max-w-40 truncate">{label}</span>
      {!disabled && (
        <button
          type="button"
          aria-label={`Remove ${label}`}
          onClick={(e) => { e.stopPropagation(); onRemove() }}
          className="ml-0.5 flex shrink-0 items-center justify-center rounded-sm opacity-50 transition-opacity hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none"
        >
          <X className="size-3" aria-hidden />
        </button>
      )}
    </span>
  )
}

function OptionRow({
  label,
  description,
  badge,
  detail,
  imageUrl,
  imageFallback,
  selected,
  onMouseDown,
}: {
  label: string
  description?: string
  badge?: React.ReactNode
  detail?: string
  imageUrl?: string
  imageFallback?: string
  selected: boolean
  onMouseDown: React.MouseEventHandler
}) {
  const row = (
    <div
      role="option"
      aria-selected={selected}
      onMouseDown={onMouseDown}
      className={cn(
        "flex cursor-pointer select-none items-center gap-2.5 rounded-sm px-2 py-1.5 text-sm",
        "transition-colors duration-150",
        "hover:bg-accent hover:text-accent-foreground",
        // A checked 16px box is the only thing separating a picked row from an unpicked
        // one, which does not survive a 19-row scroll. This is the same 5% primary wash
        // table rows use for selection, so the two surfaces speak one language.
        "aria-selected:bg-primary/5",
        // Carries attribute + pseudo-class, so it beats the equal-specificity pair above
        // and a selected row still answers the cursor instead of going inert on hover.
        "aria-selected:hover:bg-primary/10",
      )}
    >
      <Checkbox
        checked={selected}
        tabIndex={-1}
        className="pointer-events-none shrink-0"
        aria-hidden
      />
      <OptionAvatar imageUrl={imageUrl} imageFallback={imageFallback} size="md" />
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate">{label}</span>
          {badge}
        </div>
        {description ? (
          <div className="truncate text-xs text-muted-foreground">{description}</div>
        ) : null}
      </div>
    </div>
  )

  if (!description && !detail && !imageUrl && !imageFallback) {
    return row
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{row}</TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs">
        <div className="flex items-start gap-2">
          <OptionAvatar imageUrl={imageUrl} imageFallback={imageFallback} size="md" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium">{label}</span>
              {badge}
            </div>
            {description ? (
              <div className="mt-0.5 break-words text-xs opacity-90">{description}</div>
            ) : null}
            {/* Opacity rather than a grey: the tooltip surface is tinted, and grey text on a
                tinted background reads as washed out instead of secondary. */}
            {detail ? (
              <div className="mt-1 break-words text-xs opacity-70">{detail}</div>
            ) : null}
          </div>
        </div>
      </TooltipContent>
    </Tooltip>
  )
}

function AddRow({
  label,
  onMouseDown,
}: {
  label: string
  onMouseDown: React.MouseEventHandler
}) {
  return (
    <div
      onMouseDown={onMouseDown}
      className="flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-muted-foreground transition-colors duration-150 hover:bg-accent hover:text-accent-foreground"
    >
      <Plus className="size-3.5 shrink-0" aria-hidden />
      <span className="truncate">{label}</span>
    </div>
  )
}

function DropdownLoading() {
  return (
    <div className="space-y-1 p-1">
      {[0, 1, 2].map((i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  )
}
